import Vue from 'vue'
import Vuex, { Store, StoreOptions } from 'vuex'
import { AuthState, FoodOrderingState, GlobalState, NavigationState } from '@/types/state'
import * as getterTypes from '@/store/getter-types'
import * as mutationTypes from '@/store/mutation-types'
import * as actionTypes from '@/store/action-types'
import authModule from '@/store/modules/auth'
import settingsModule from '@/store/modules/settings'
import navigationModule from '@/store/modules/navigation'
import foodOrderingModule from '@/store/modules/food-ordering'
import axios from '@/plugins/axios'
import { AxiosResponse } from 'axios'
import { AccountEmail } from '@/model/AccountEmail'
import {
  ChangeNewPasswordPayload,
  ChangePasswordPayload,
  FilterEventsPayload, NewRequestPayload,
  UpdateAccountEmail,
  UserPayload
} from '@/types/payload'
import {Dashboard} from "@/types";
import moment from "moment";
import {CreditDetails} from "@/model/CreditDetails";
import CalendarEventForDay from "@/model/CalendarEventForDay";
import {CurrentUser} from "@/model/CurrentUser";
import {FETCH_CUSTOMER_DATA, UPDATE_PERSON_CREDENTIAL_PASS_BY_ID} from "@/store/action-types";
import {FETCH_RESERVATION_BY_ID} from "@/store/action-types";
import {CURRENT_LANGUAGE_FIELD} from "@/types/constants";
import i18n from "@/plugins/i18n";
import vuetify from "@/plugins/vuetify";
import Language from "@/model/Language";
import SmartLink from "@/model/SmartLink";
import CustomerWithGroup from "@/model/CustomerWithGroup";

Vue.use(Vuex)

const supportedLanguages: Language[] = [
  Language.create('sk', i18n.t('languages.slovakLanguageLabel').toString(), false),
  //Language.create('en', i18n.t('languages.englishLanguageLabel').toString(), false),
  Language.create('cs', i18n.t('languages.czechLanguageLabel').toString(), false),

]

const state = (): GlobalState => ({
  auth: {} as AuthState,
  navigation: {} as NavigationState,
  foodOrdering: {} as FoodOrderingState,
  showAppDrawer: false,
  dataChanged: false,
  showErrorResponseDialog: false,
  errorResponseDialogTitle: undefined,
  errorResponseDialogResponse: undefined,
  errorResponseDialogInfoText: undefined,
  showProfileDialog: false,
  events: [],
  mode: '',
  buttonsMode: true,
  cid: '',
  tt: '',
  logOutButton: false,
  showLogOutTimer: false,
  supportedLanguages,
  currentUiLanguage: supportedLanguages[0],
})

const storeOptions: StoreOptions<any> = {
  modules: {
    auth: authModule,
    settings: settingsModule,
    navigation: navigationModule,
    foodOrdering: foodOrderingModule,
  },

  state,

  getters: {
    [getterTypes.SUPPORTED_LANGUAGES] (state: GlobalState): Language[] {
      return state.supportedLanguages
    },

    [getterTypes.CURRENT_UI_LANGUAGE] (state: GlobalState): Language {
      return state.currentUiLanguage
    },

    [getterTypes.IS_USER_LOGGED_IN] (state: GlobalState): boolean {
      return !!state.auth.currentUser
    },

    [getterTypes.IS_DEBTOR] (state: GlobalState): boolean {
      return !!state.auth.currentUser?.debtor
    },

    [getterTypes.APP_DRAWER_STATE] (state: GlobalState): boolean {
      return !!state.showAppDrawer
    },

    [getterTypes.IS_DATA_CHANGED] (state: GlobalState): boolean {
      return state.dataChanged
    },

    [getterTypes.IS_MOBILE] (state: GlobalState): boolean {
      let check = false;
      (function (a) {
        if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) check = true
      })(navigator.userAgent || navigator.vendor)
      return check
    },

    [getterTypes.SHOW_ERROR_RESPONSE_DIALOG] (state: GlobalState): boolean {
      return state.showErrorResponseDialog
    },

    [getterTypes.ERROR_RESPONSE_DIALOG_DATA] (state: GlobalState): any {
      return {
        title: state.errorResponseDialogTitle,
        errorResponse: state.errorResponseDialogResponse,
        infoText: state.errorResponseDialogInfoText
      }
    },

    [getterTypes.IS_PROFILE_DIALOG_SHOW] (state: GlobalState): boolean {
      return state.showProfileDialog
    },

    [getterTypes.GET_CALENDAR_EVENTS] (state: GlobalState): CalendarEventForDay[] {
      return state.events
    },

    [getterTypes.GET_CONFIG] (state: GlobalState): any {
      return {
        topGroupIsExclusive: true,
        tagConfig: [{
          tagId:1,
          selection:'single',
          mandatory: true,
          title: 'polievka'
        },
          {
            tagId:5,
            selection:'single',
            mandatory: true,
            title: 'hlavné jedlo'
          },
          {
            tagId:8,
            selection:'single',
            mandatory: true,
            title: "bageta"
          },
          {
            tagId:11,
            selection:'single',
            mandatory: true,
            title: "minútka"
          },

          {//omacka nepovinna
            tagId:6,
            selection:'single',
            mandatory: false,
            title: "omáčka"
          },

          {
            tagId:11,
            selection:'single',
            mandatory: true,
            title: "šalát"
          },


        ]
      }
    },

    [getterTypes.GET_MODE] (state: GlobalState): string | null {
      return state.mode
    },

    [getterTypes.GET_SHOW_CONTROL_BUTTONS_MODE] (state: GlobalState): boolean {
      return state.buttonsMode
    },

    [getterTypes.GET_CID] (state: GlobalState): string {
      return state.cid
    },

    [getterTypes.GET_TT] (state: GlobalState): string {
      return state.tt
    },

    [getterTypes.GET_SHOW_LOGOUT_BUTTON_MODE] (state: GlobalState): boolean {
      return state.logOutButton
    },

    [getterTypes.GET_SHOW_LOGOUT_TIMER_MODE] (state: GlobalState): boolean {
      return state.showLogOutTimer
    },
  },

  mutations: {
    [mutationTypes.SET_CURRENT_UI_LANGUAGE] (state: GlobalState, payload: Language): void {
      state.currentUiLanguage = payload
      localStorage.setItem(CURRENT_LANGUAGE_FIELD, payload.code)
    },

    [mutationTypes.TOGGLE_APP_DRAWER_MUTATION] (state: GlobalState): void {
      state.showAppDrawer = !state.showAppDrawer
    },

    [mutationTypes.SET_DRAWER_STATE_MUTATION] (state: GlobalState, payload: boolean): void {
      state.showAppDrawer = payload
    },

    [mutationTypes.SET_DATA_CHANGED] (state: GlobalState, payload: boolean): void {
      state.dataChanged = payload
    },

    [mutationTypes.SET_ERROR_RESPONSE_DIALOG_DATA] (state: GlobalState, payload: any): void {
      state.showErrorResponseDialog = payload.show
      state.errorResponseDialogTitle = payload.title
      state.errorResponseDialogResponse = payload.errorResponse
      state.errorResponseDialogInfoText = payload.infoText
    },

    [mutationTypes.SHOW_PROFILE_DIALOG_MUTATION] (state: GlobalState, payload: boolean): void {
      state.showProfileDialog = payload
    },

    [mutationTypes.SET_CALENDAR_EVENTS] (state: GlobalState, payload: CalendarEventForDay[]) {
      state.events = payload
    },

    [mutationTypes.SET_MODE_MUTATION] (state: GlobalState, payload: string): void {
      state.mode = payload
    },

    [mutationTypes.SET_CONTROL_BUTTONS_MODE_MUTATION] (state: GlobalState, payload: boolean): void {
      state.buttonsMode = payload
    },

    [mutationTypes.SET_CID_MODE_MUTATION] (state: GlobalState, payload: string): void {
      state.cid = payload
    },

    [mutationTypes.SET_TT_MODE_MUTATION] (state: GlobalState, payload: string): void {
      state.tt = payload
    },

    [mutationTypes.SET_SHOW_LOGOUT_BUTTON_MODE_MUTATION] (state: GlobalState, payload: boolean): void {
      state.logOutButton = payload
    },

    [mutationTypes.SET_SHOW_LOGOUT_TIMER_MODE_MUTATION] (state: GlobalState, payload: boolean): void {
      state.showLogOutTimer = payload
    }
  },

  actions: {
    [actionTypes.SWITCH_APPLICATION_LANGUAGE] ({ commit }: { commit: Function }, payload: Language): void {
      vuetify.framework.lang.current = payload.code
      vuetify.framework.rtl = payload.rtl

      i18n.locale = payload.code

      if (this.getters[getterTypes.CURRENT_UI_LANGUAGE] !== payload) {
        commit(mutationTypes.SET_CURRENT_UI_LANGUAGE, payload)
      }
    },
    [actionTypes.TOGGLE_APP_DRAWER] ({ commit }: { commit: Function }): void {
      commit(mutationTypes.TOGGLE_APP_DRAWER_MUTATION)
    },

    [actionTypes.SET_DRAWER_STATE] ({ commit }: { commit: Function }, payload: boolean): void {
      commit(mutationTypes.SET_DRAWER_STATE_MUTATION, payload)
    },

    [actionTypes.SHOW_RESPONSE_ERROR_DIALOG] ({ commit }: { commit: Function }, payload: any): void {
      commit(mutationTypes.SET_ERROR_RESPONSE_DIALOG_DATA, payload)
    },

    [actionTypes.HIDE_RESPONSE_ERROR_DIALOG] ({ commit }: { commit: Function }): void {
      commit(mutationTypes.SET_ERROR_RESPONSE_DIALOG_DATA, {
        show: false,
        title: undefined,
        errorResponse: undefined
      })
    },

    [actionTypes.SHOW_PROFILE_DIALOG] ({ commit }: { commit: Function }, payload: boolean): void {
      commit(mutationTypes.SHOW_PROFILE_DIALOG_MUTATION, payload)
    },

    [actionTypes.SET_MODE] ({ commit }: { commit: Function }, payload: string): void {
      commit(mutationTypes.SET_MODE_MUTATION, payload)
    },

    [actionTypes.SET_CONTROL_BUTTONS_MODE] ({ commit }: { commit: Function }, payload: string): void {
      commit(mutationTypes.SET_CONTROL_BUTTONS_MODE_MUTATION, payload)
    },

    [actionTypes.SET_CID_MODE] ({ commit }: { commit: Function }, payload: string): void {
      commit(mutationTypes.SET_CID_MODE_MUTATION, payload)
    },

    [actionTypes.SET_TT_MODE] ({ commit }: { commit: Function }, payload: string): void {
      commit(mutationTypes.SET_TT_MODE_MUTATION, payload)
    },

    [actionTypes.SHOW_LOGOUT_BUTTON_MODE] ({ commit }: { commit: Function }, payload: boolean): void {
      commit(mutationTypes.SET_SHOW_LOGOUT_BUTTON_MODE_MUTATION, payload)
    },

    [actionTypes.SHOW_LOGOUT_TIMER_MODE] ({ commit }: { commit: Function }, payload: boolean): void {
      commit(mutationTypes.SET_SHOW_LOGOUT_TIMER_MODE_MUTATION, payload)
    },

    async [actionTypes.FETCH_ACCOUNT_EMAILS]({}): Promise<AccountEmail[]> {
      let headers = {
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      }

      const response: AxiosResponse = await axios.get('/api/account/emails', {headers: headers})
      return AccountEmail.createFromResponse(response.data)
    },

    async [actionTypes.UPDATE_ACCOUNT_EMAILS]({}, payload: UpdateAccountEmail[]): Promise<AccountEmail[]> {
      let headers = {
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      }

      const response: AxiosResponse = await axios.put('/api/account/emails', payload, {headers: headers})
      return AccountEmail.createFromResponse(response.data)
    },

    async [actionTypes.FETCH_ACCOUNT_USER]({commit}: { commit: Function }): Promise<void> {
      let headers = {
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      }

      const response: AxiosResponse = await axios.get('/api/account', {headers: headers})
      const user = CurrentUser.createFromUserInformationResponse({
        id: response.data.id,
        debtor: response.data.boarderList[0].debtor,
        givenName: response.data.firstName,
        familyName: response.data.familyName,
        isBoarder: response.data.isBoarder,
        qrCode: response.data.qrCode,
        isStaff: response.data.isStaff,
        staffRoleList: response.data.staffRoleList,
        boarderList: response.data.boarderList,
        loginName: response.data.loginName,
        tenant: response.data.tenant,
        phoneNumbers: response.data.phoneNumbers,
        currentCreditBalance: response.data.currentCreditBalance
      })

      commit(mutationTypes.AUTH_SET_CURRENT_USER_MUTATION, user)
    },

    async [actionTypes.UPDATE_ACCOUNT_USER]({commit}: { commit: Function }, payload: UserPayload): Promise<void> {
      let headers = {
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      }

      await axios.put('/api/account', payload, {headers: headers})
    },

    async [actionTypes.FETCH_DASHBOARD]({}): Promise<Dashboard> {
      let headers = {
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      }

      const response: AxiosResponse = await axios.get('/api/dashboard',{headers: headers})
      return response.data
    },

    async [actionTypes.FETCH_CALENDAR_EVENTS_FOR_DAY] ({ commit }: { commit: Function }, payload: FilterEventsPayload): Promise<CalendarEventForDay[]> {
      let headers = {
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      }

      const response = await axios.get(`/api/calendar/global-event`, {headers: headers,
        params: {
          "date[after]": moment(payload.after).format('YYYY-MM-DD'),
          "date[before]": moment(payload.before).format('YYYY-MM-DD')
        }})

      const calendarEvents: CalendarEventForDay[] = CalendarEventForDay.createCalendarEvent(response.data)

      commit(mutationTypes.SET_CALENDAR_EVENTS, calendarEvents)

      return calendarEvents
    },

    async [actionTypes.FETCH_CREDIT_DETAILS]({}): Promise<CreditDetails[]> {
      let headers = {
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      }

      const response: AxiosResponse = await axios.get('/api/account/wallet-entry',{headers: headers})
      return CreditDetails.createFromResponse(response.data)
    },
    async [actionTypes.INIT_STORE]({commit, dispatch}: { commit: Function, dispatch: Function }): Promise<void> {

      await Promise.all([
        dispatch(actionTypes.AUTH_INIT_MODULE),
      ])
    },

    async [actionTypes.FETCH_RESERVATION_BY_ID] ({  },payload: any): Promise<any[]> {
      const response = await axios.get(`/api/food-ordering/offer-reservation/${payload}`, {
      })
      return response.data
    },

    async [actionTypes.FETCH_TENANT_NAME] ({  }): Promise<any> {
      const response = await axios.get('/api/tenant-settings')

      return response.data
    },

    async [actionTypes.CUSTOMER_FETCH_CUSTOMER_LIST] ({ rootState }: { rootState: GlobalState }): Promise<any[]> {
      const tenantCode: string = rootState.auth.currentUser!.tenant.code

      const response = await axios.get(`/api/${tenantCode}/customer`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      return response.data
    },

    async [actionTypes.BOARDER_FETCH_BOARDER_GROUP_LIST]({rootState}: { rootState: GlobalState }): Promise<any[]> {
      const tenantCode: string = rootState.auth.currentUser!.tenant.code

      const response = await axios.get(`/api/${tenantCode}/boarder-group`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      return response.data
    },

    async [actionTypes.UPDATE_PERSON_CREDENTIAL_PASS_BY_ID] ({ commit }: { commit: Function}, payload: ChangeNewPasswordPayload): Promise<void> {
      await axios.put(`/api/account/password`,
          {
            "password": payload.password,
          })
    },

    async [actionTypes.CREATE_NEW_REQUEST] ({  }, payload: NewRequestPayload): Promise<any> {

      const response = await axios.post('api/request/anonymous', {
        tenant: payload.tenant,
        kind: payload.kind,
        data: {
          firstName: payload.data.firstName,
          familyName: payload.data.familyName,
          email: payload.data.email,
          phoneNumber: payload.data.phoneNumber,
          customer: payload.data.customer,
          boarderGroup: payload.data.boarderGroup,
          alreadyHaveAccount: payload.data.alreadyHaveAccount
        }

          }
      )
      return response.data
    },

    async [actionTypes.FETCH_BOARDER_DATA] ({}, payload: any): Promise<CustomerWithGroup[]> {
      const response = await axios.get(`api/customer/with-groups?tenant=/api/tenant/${payload.replace('/api/tenant/','')}`)
      return response.data
    },

    async [actionTypes.FETCH_SMART_LINK] ({},payload: string): Promise<SmartLink[]> {
      const response = await axios.get(`api/smart-link/slug/${payload}`)
      return response.data
    },

    async [actionTypes.DELETE_BOARDER]({}, payload: any): Promise<any> {

      const response = await axios.put(`/api/account/${payload}/terminate`)

      return response.data
    },

    async [actionTypes.FETCH_CUSTOMER_DATA] ({},payload: string): Promise<any> {
      const response = await axios.get(`api/${payload}/customer`)
      return response.data
    },
  }
}

export default new Store(storeOptions)
